





































































































































































































































































import { Vue, Component } from 'vue-property-decorator';
import { mapState } from 'vuex';

import { objectIsEmpty } from '~/utils/common.js';
import { eventType, getSportTags, replaceUrl } from '~/utils/explorer.js';

import ChevronRightSolid from '~/components/icons/ChevronRightSolid.vue';
import ProfileRunSportTag from '~/components/partials/ProfileRunSportTag.vue';
import EventCategoryTag from '~/components/partials/EventCategoryTag.vue';
import CalendarSolid from '~/components/icons/CalendarSolid.vue';
import EuroSolid from '~/components/icons/EuroSolid.vue';
import PinOutline from '~/components/icons/PinOutline.vue';
import EventFeed from '~/components/lists/EventFeed.vue';
import PlaySolid from '~/components/icons/PlaySolid.vue';
import ExternalOutline from '~/components/icons/ExternalOutline.vue';
import RelatedDestinationItem from '~/components/lists/items/RelatedDestinationItem.vue';
import ProvidersFeed from '~/components/lists/ProvidersFeed.vue';
import Breadcrumb from '~/components/partials/Breadcrumb.vue';
import WarningOutline from '~/components/icons/WarningOutline.vue';
import FacebookSolid from '~/components/icons/FacebookSolid.vue';
import ShareSolid from '~/components/icons/ShareSolid.vue';
import DatesRanges from '~/components/partials/DatesRanges.vue';
import LabelsInfos from "~/components/partials/LabelsInfos.vue";

@Component({
  components: {
    LabelsInfos,
    ChevronRightSolid,
    ProfileRunSportTag,
    EventCategoryTag,
    CalendarSolid,
    EuroSolid,
    PinOutline,
    EventFeed,
    PlaySolid,
    ExternalOutline,
    RelatedDestinationItem,
    ProvidersFeed,
    Breadcrumb,
    WarningOutline,
    FacebookSolid,
    ShareSolid,
    DatesRanges,
  },
  middleware: ['noauth'],
  computed: {
    ...mapState('explorer', [
      'event',
      'destinationEventsSuggestions',
    ]),
    ...mapState('lang', [
      'locale',
    ]),
  },
  head () {
    const self: any = this;
    const description = self.event?.description?.trim().replace(/<\/?[^>]+(>|$)/g, "");
    const locale: string = self.$store.state.lang.locale;

    return {
      title: `${self.event.title} - On Piste`,
      meta: [
        {
          hid: 'description',
          name: 'description',
          content: description,
        },
        {
          hid: 'og:title',
          property: 'og:title',
          content: `${self.event.title} - On Piste`,
        },
        {
          hid: 'og:description',
          property: 'og:description',
          content: description,
        },
        {
          hid: 'og:image',
          property: 'og:image',
          content: self.event.media?.length ? self.$thumbnailUrl(self.event.media[0], 1200) : 'https://onpiste.com/img/placeholders/event-placeholder.png',
        },
        {
          hid: 'og:url',
          property: 'og:url',
          content: `https://www.onpiste.com/${locale === 'fr' ? '' : `${locale}/`}explorer/events/${self.$slugify(self.event.title)}-${self.event.id}`,
        },
      ],
    };
  },
  async fetch () {
    const self: any = this;
    await self.addDestinationsEvents();
  },
  async asyncData ({ store, route, error, app }) {
    const errorInstance: any = error;

    if (
      route.params.eventid.split('-').pop() === '' ||
      isNaN(Number(route.params.eventid.split('-').pop()?.trim()))
    ) {
      return errorInstance({
        statusCode: 404,
        response: { data: { detail: 'Unknown event' } },
      });
    }

    try {
      if (route.query?.preview) {
        await store.dispatch('explorer/getEventPreview', {
          id: route.params.eventid.split('-').pop()?.trim(),
          token: route.query.preview,
          lang: store.state.lang.locale,
        });
      } else {
        await store.dispatch('explorer/getEvent', {
          id: route.params.eventid.split('-').pop()?.trim(),
          lang: store.state.lang.locale,
        });
      }
    } catch (e) {
      return errorInstance({
        statusCode: e.response.status,
        response: { data: { detail: app.i18n.t(e.response.data.detail) } },
      });
    }
  },
})
export default class EventId extends Vue {
  destinationEventsSuggestions: Types.Event[];
  locale: string;
  event: Types.Event;

  hashtags: Array<string> = ['Rossignol', 'On Piste'];

  /* Returns translated sports tags of event sports using util */
  get sportsSliced () {
    return getSportTags(this.event.sports.slice(0));
  }

  /* Returns translated event type using util */
  get eventType () {
    return eventType(this.event?.type);
  }

  /* Computed method to return complete URL of page for ShareNetwork button */
  get urlPage () {
    if (process.client) return window.location.href;
    return '';
  }

  /* Returns reactive array of breadcrumb links containing some store values */
  get breadcrumbMenu () {
    return [
      { path: '/', name: this.$t('explorer.events.breadcrumb.home') },
      { path: '/explorer?tab=events', name: this.$t('explorer.events.breadcrumb.explorer') },
      { path: '', name: this.event?.title, isActive: true },
    ];
  }

  /* Returns reactive submenu links that contains some store values */
  get subMenuLinks () {
    return [
      { link: '#description', name: this.$t('explorer.events.submenu.description'), isDisplayed: true },
      { link: '#similar-events', name: this.$t('explorer.events.submenu.similar'), isDisplayed: this.destinationEventsSuggestions?.length },
    ];
  }

  /*
    This computed constructs a link with every sport of event in query, so a
    filter will be applied when going to /explorer page on events tab.
  */
  get linkToEvents () {
    let query: string = '';

    if (this.event) {
      for (let i = 0; i < this.event?.sports?.length; i++) {
        query += `&sport=${this.event?.sports[i].title}`;
      }
      return `/explorer?tab=events${query}`;
    }
    return '#';
  }

  /* Computed skeleton handle */
  get eventIsEmpty () {
    return objectIsEmpty(this.event);
  }

  /*
    This computed adapts itself to the format of event dates:

    - If date range type is Weekly, we will check if the last date of the range
      is earlier than right now. If yes, event has passed.

    - If date range is type Ranges, we will check if the last date of the last
      range is earlier than right now. If yes, event has passed.
  */
  get newsHasPassed () {
    if (this.event.dates?.type === 'weekly') {
      const dateEvent = this.$dayjs(this.event.dates.range.end);
      const dateNow = this.$dayjs();
      return dateEvent.diff(dateNow, 'd') < 0;
    } else if (this.event.dates?.type === 'ranges') {
      const dateEvent = this.$dayjs(this.event.dates.ranges[this.event.dates.ranges.length - 1]?.end);
      const dateNow = this.$dayjs();
      return dateEvent.diff(dateNow, 'd') < 0;
    }
    return false;
  }

  mounted () {
    this.event.destinations.forEach(destination => window.dataLayer.push({
      'Destination': destination.title,
      'DestinationID': destination.id,
    }));

    /*
      Using util method to adapt URL to a specific format containing title and
      ID of retrieved element for better SEO.
    */
    if (process.client) {
      replaceUrl(
        this.$route.params.eventid,
        `${this.locale !== 'fr' ? `/${this.locale}` : ''}/explorer/events/${this.$slugify(this.event.title)}`,
        this.$slugify(this.event?.title),
      );
    }
  }

  /* Image fallback if card <img @error> is triggered */
  imageFallback (event: any) {
    event.target.src = "/img/placeholders/event-placeholder.png";
  }

  /*
    In this page, sports tags are clickable and redirect to explorer.
    We will construct the link of the button using sport id to filter elements
    when moving to explorer page.
  */
  redirectToExplorer (tag: Types.SportItem) {
    const route: any = this.localePath(`/explorer?sport=${tag.id}`);
    this.$router.push(route);
  }

  /*
    In SSR mode this method is called in fetch, before the page mounts.
    The tmeplate wil be rendered with datas of element.
  */
  async addDestinationsEvents () {
    await this.$store.dispatch('explorer/getEventsSimilar', {
      id: this.event.id,
      lang: this.locale,
    });
  }
}
